'use client';

import { useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { KeyRound } from 'lucide-react';
import { useForm } from 'react-hook-form';

import { useSignInWithSSO } from '@kit/supabase/hooks/use-sign-in-with-sso';
import { Button } from '@kit/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@kit/ui/form';
import { If } from '@kit/ui/if';
import { Input } from '@kit/ui/input';
import { Trans } from '@kit/ui/trans';

import { SsoSignInSchema } from '../schemas/sso-sign-in.schema';
import { AuthErrorAlert } from './auth-error-alert';

/**
 * SAML 2.0 single sign-on.
 *
 * ## Why this is a disclosure and not a button
 *
 * Every other method on this screen is offered to everyone. SSO is offered to
 * the staff of the handful of customers who have configured an identity
 * provider, and asking everybody else for "your company domain" would be a
 * field most people cannot answer. So it stays behind one line of text until
 * somebody says that is what they came for.
 *
 * ## The redirect is ours to perform
 *
 * `signInWithSSO` does not navigate. It resolves with the identity provider's
 * URL and leaves the caller to go there, which means the mutation settling is
 * NOT success — the flow has barely begun. `window.location.assign` rather than
 * the Next router: the destination is a different origin entirely, and a client
 * router push would be a no-op.
 *
 * The pending state is therefore never cleared on the happy path. That is
 * correct: the button must stay disabled while the browser is on its way out,
 * or a second click starts a second flow.
 */
export function SsoSignInContainer(props: { paths: { callback: string } }) {
  const [expanded, setExpanded] = useState(false);
  const signInWithSso = useSignInWithSSO();

  const form = useForm({
    resolver: zodResolver(SsoSignInSchema),
    defaultValues: {
      identifier: '',
    },
  });

  const onSubmit = async ({ domain }: { domain: string }) => {
    const redirectTo = new URL(
      props.paths.callback,
      window.location.origin,
    ).toString();

    const { url } = await signInWithSso.mutateAsync({
      domain,
      options: { redirectTo },
    });

    if (url) {
      window.location.assign(url);
    }
  };

  if (!expanded) {
    return (
      <Button
        type={'button'}
        variant={'link'}
        size={'sm'}
        data-test={'sso-disclosure-button'}
        onClick={() => setExpanded(true)}
      >
        <Trans i18nKey={'auth.sso.disclosure'} />
      </Button>
    );
  }

  return (
    <Form {...form}>
      <form
        className={'w-full'}
        data-test={'sso-sign-in-form'}
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <div className={'flex flex-col space-y-4'}>
          <If condition={signInWithSso.error}>
            <AuthErrorAlert error={signInWithSso.error} />
          </If>

          <FormField
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <Trans i18nKey={'auth.sso.identifierLabel'} />
                </FormLabel>

                <FormControl>
                  <Input
                    data-test={'sso-identifier-input'}
                    autoComplete={'organization'}
                    placeholder={'you@company.example'}
                    {...field}
                  />
                </FormControl>

                <FormMessage />
              </FormItem>
            )}
            name={'identifier'}
          />

          <Button
            type={'submit'}
            variant={'outline'}
            data-test={'sso-submit-button'}
            disabled={signInWithSso.isPending || signInWithSso.isSuccess}
          >
            <KeyRound className={'h-4'} />

            <If
              condition={signInWithSso.isPending || signInWithSso.isSuccess}
              fallback={<Trans i18nKey={'auth.sso.submit'} />}
            >
              <Trans i18nKey={'auth.sso.redirecting'} />
            </If>
          </Button>
        </div>
      </form>
    </Form>
  );
}
